import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface EnhancementFormat {
  format_key: string;
  label: string;
  description: string | null;
  sort_order: number;
}

export interface EnhancementToggle {
  format_key: string;
  toggle_key: string;
  label: string;
  description: string | null;
  default_enabled: boolean;
  sort_order: number;
  column_name: string;
  column_type: string;
}

export interface EnhancementConfig {
  formats: EnhancementFormat[];
  toggles: EnhancementToggle[];
}

async function fetchEnhancementConfig(): Promise<EnhancementConfig> {
  const [formatsRes, togglesRes] = await Promise.all([
    supabase
      .from("enhancement_format_definitions")
      .select("format_key, label, description, sort_order")
      .order("sort_order"),
    supabase
      .from("enhancement_toggle_definitions")
      .select("format_key, toggle_key, label, description, default_enabled, sort_order, column_name, column_type")
      .order("sort_order"),
  ]);

  if (formatsRes.error) throw formatsRes.error;
  if (togglesRes.error) throw togglesRes.error;

  const formats = formatsRes.data as EnhancementFormat[];
  const toggles = togglesRes.data as EnhancementToggle[];

  if (formats.length < 6) {
    console.error(`REGRESSION WARNING: Expected 6 enhancement formats, got ${formats.length}`);
  }

  return { formats, toggles };
}

export function useEnhancementConfig() {
  return useQuery<EnhancementConfig>({
    queryKey: ["enhancement-config"],
    queryFn: fetchEnhancementConfig,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60,
  });
}
